import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Check, Building2, Users, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { EnterpriseQuoteModal } from "../components/EnterpriseQuoteModal";

const TIERS = [
  {
    id: "tier10",
    name: "Tier 10",
    desc: "Per piccole realtà",
    seats: "fino a 10",
    monthly: 20.00,
    setup: 199.00,
    perEmp: 1.00,
    features: ["Calendario intelligente", "QR + Geolocalizzazione", "Cedolini & ferie", "Export paghe CSV", "Supporto email"],
    highlight: false,
  },
  {
    id: "tier20",
    name: "Tier 20",
    desc: "Per team in crescita",
    seats: "fino a 20",
    monthly: 40.00,
    setup: 199.00,
    perEmp: 1.00,
    features: ["Tutto Tier 10 +", "Rimborsi spese", "Firma digitale base", "Multi-utente admin", "Supporto prioritario"],
    highlight: false,
  },
  {
    id: "tier30",
    name: "Tier 30",
    desc: "Il più scelto dalle PMI",
    seats: "fino a 30",
    monthly: 60.00,
    setup: 299.00,
    perEmp: 1.00,
    features: ["Tutto Tier 20 +", "AI analisi presenze", "Trasferte GPS", "Turni & Timesheet", "Anomalie automatiche"],
    highlight: true,
  },
  {
    id: "tier50",
    name: "Tier 50",
    desc: "Per aziende strutturate",
    seats: "fino a 50",
    monthly: 100.00,
    setup: 399.00,
    perEmp: 1.00,
    features: ["Tutto Tier 30 +", "Multi-sede illimitate", "Report avanzati", "Onboarding dedicato", "Customer Success"],
    highlight: false,
  },
];

export default function Pricing({ onDemo }) {
  const [yearly, setYearly] = useState(false);
  const [plans, setPlans] = useState({});
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const getPlan = (tier) => plans[`${tier}_${yearly ? "yearly" : "monthly"}`];

  const handleBuy = async (tier) => {
    if (!user) {
      toast.info("Accedi per procedere all'acquisto");
      nav("/register");
      return;
    }
    const planId = `${tier}_${yearly ? "yearly" : "monthly"}`;
    try {
      const r = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch {
      toast.error("Errore creazione checkout");
    }
  };

  return (
    <section id="pricing" className="bg-white" data-testid="pricing">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">Piani e prezzi</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Trasparenti.<br/><span className="text-[#757575]">Senza sorprese.</span></h2>
            <p className="mt-4 text-sm text-[#666] max-w-xl">Setup una tantum + canone mensile + €1 per ogni dipendente attivo. Nessun costo nascosto.</p>
          </div>
          <div className="md:col-span-5 flex md:justify-end items-center gap-4 text-sm">
            <span className={!yearly ? "font-semibold" : "text-[#757575]"}>Mensile</span>
            <Switch checked={yearly} onCheckedChange={setYearly} data-testid="pricing-toggle"/>
            <span className={yearly ? "font-semibold flex items-center gap-2" : "text-[#757575] flex items-center gap-2"}>
              Annuale <span className="text-xs bg-[#FF7A29] text-white px-2.5 py-1 rounded-full font-semibold">-10%</span>
            </span>
          </div>
        </div>

        {/* 4 piani */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => {
            const p = getPlan(t.id);
            const displayPrice = p ? p.price : t.monthly;
            const monthlyEq = p?.monthly_equivalent || t.monthly;
            return (
              <div
                key={t.id}
                data-testid={`plan-card-${t.id}`}
                className={`card-smart p-6 md:p-7 relative flex flex-col ${t.highlight ? "bg-[#0A0A0A] text-white border-[#0A0A0A] shadow-xl shadow-black/30 lg:-translate-y-3" : ""}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF7A29] text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold shadow-lg shadow-orange-500/40">Più scelto</div>
                )}
                <div className="font-display text-xl mb-1">{t.name}</div>
                <p className={`text-xs mb-4 ${t.highlight ? "text-white/70" : "text-[#666]"}`}>{t.desc}</p>

                <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full mb-4 w-fit ${t.highlight ? "bg-[#FF7A29] text-white" : "bg-[#FFF0E3] text-[#E85D00]"}`}>
                  <Users className="w-3 h-3"/> {t.seats} dipendenti
                </div>

                <div className="mb-1 flex items-baseline">
                  <span className={`font-display text-4xl tracking-tighter ${t.highlight ? "text-[#FF7A29]" : "text-[#0A0A0A]"}`}>€{displayPrice.toFixed(2)}</span>
                  <span className={`ml-1.5 text-xs ${t.highlight ? "text-white/60" : "text-[#757575]"}`}>/{yearly ? "anno" : "mese"}</span>
                </div>
                {yearly && (
                  <div className={`text-[10px] mb-3 ${t.highlight ? "text-white/50" : "text-[#999]"}`}>
                    Equiv. €{monthlyEq.toFixed(2)}/mese
                  </div>
                )}

                {/* Setup + per-employee chips */}
                <div className={`text-[11px] space-y-1.5 mb-5 mt-2 ${t.highlight ? "text-white/80" : "text-[#555]"}`}>
                  <div className="flex items-center justify-between border-b border-dashed pb-1.5" style={{borderColor: t.highlight ? "rgba(255,255,255,0.1)" : "#EDE5D8"}}>
                    <span>Setup una tantum</span>
                    <strong className={t.highlight ? "text-white" : "text-[#0A0A0A]"}>€{t.setup.toFixed(2)}</strong>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Per dipendente</span>
                    <strong className={t.highlight ? "text-white" : "text-[#0A0A0A]"}>€{t.perEmp.toFixed(2)}/mese</strong>
                  </div>
                </div>

                <Button
                  onClick={() => handleBuy(t.id)}
                  data-testid={`plan-buy-${t.id}`}
                  className={`w-full rounded-full h-11 text-sm font-semibold mb-5 ${t.highlight ? "bg-[#FF7A29] text-white hover:bg-white hover:text-[#0A0A0A] btn-glow" : "bg-[#0A0A0A] hover:bg-[#FF7A29] text-white"}`}
                >
                  Scegli {t.name}
                </Button>

                <ul className="space-y-2 text-xs flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className={`mt-0.5 shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center ${t.highlight ? "bg-[#FF7A29]" : "bg-[#FFF0E3]"}`}>
                        <Check className={`w-2 h-2 ${t.highlight ? "text-white" : "text-[#FF7A29]"}`} strokeWidth={3}/>
                      </span>
                      <span className={t.highlight ? "text-white/85" : "text-[#333]"}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Enterprise CTA — > 50 dipendenti */}
        <div className="mt-10 relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A] text-white p-8 md:p-12" data-testid="enterprise-cta">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#FF7A29] rounded-full opacity-20 blur-3xl"/>
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#E85D00] rounded-full opacity-10 blur-3xl"/>
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#FF7A29]/15 text-[#FF7A29] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full mb-4">
                <Building2 className="w-3 h-3"/> Enterprise · &gt; 50 dipendenti
              </div>
              <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.1] mb-3">
                Hai più di 50 dipendenti?<br/>
                <span className="text-[#FF7A29]">Costruiamo il piano su misura.</span>
              </h3>
              <p className="text-sm md:text-base text-white/70 mb-6 max-w-xl leading-relaxed">
                Pricing dedicato, integrazione SSO/Active Directory, API personalizzate, integrazioni paghe complete (Zucchetti, TeamSystem, Inaz) e Customer Success dedicato. Risposta in 24 ore lavorative.
              </p>
              <div className="flex flex-wrap gap-2 mb-6 text-[11px] text-white/60">
                {["SSO / SAML", "API REST", "SLA 99,9%", "Onboarding guidato", "Multi-tenant"].map((b) => (
                  <span key={b} className="px-2.5 py-1 rounded-full bg-white/10 border border-white/15">{b}</span>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Button
                onClick={() => setQuoteOpen(true)}
                data-testid="enterprise-quote-btn"
                className="rounded-full bg-[#FF7A29] hover:bg-white hover:text-[#0A0A0A] text-white h-14 px-8 text-base btn-glow shadow-2xl shadow-orange-500/40 group"
              >
                Richiedi preventivo
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"/>
              </Button>
              <p className="text-[10px] text-white/40 mt-3">Nessun impegno · Risposta in 24h</p>
            </div>
          </div>
        </div>

        <p className="text-xs text-[#757575] text-center mt-8">
          IVA esclusa. Fatturazione in Euro. Setup fee fatturato al primo accesso · canone mensile addebitato dal mese successivo · €1/dipendente attivo conteggiato a fine mese.
        </p>
      </div>

      <EnterpriseQuoteModal open={quoteOpen} onOpenChange={setQuoteOpen}/>
    </section>
  );
}
